import type { Breadcrumb, BrowserOptions, ErrorEvent } from "@sentry/react";
import { REDACTED, TOKEN_PATTERNS, createRedactor, failClosed, passesLuhn } from "@speakai/common/src/observability/scrub";

type StreamedSpan = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];
type LogEntry = Parameters<NonNullable<BrowserOptions["beforeSendLog"]>>[0];

const MAX_STRING_LENGTH = 16 * 1024;
const MAX_DEPTH = 8;

// Credential names that appear as key=value, key: value, or in a query string or URL fragment.
const CREDENTIAL_NAMES =
  "X-Amz-(?:Signature|Security-Token|Credential)|AWSAccessKeyId|Policy|Key-Pair-Id|Signature|sig|token|access_?token|refresh_?token|id_?token|client_?secret|api_?key|x-speakai-key|key|password|passwd|secret";
// The name must not continue an identifier; the value is a single linear character class.
const CREDENTIAL_PAIR = new RegExp(`(?<![A-Za-z0-9_-])((?:${CREDENTIAL_NAMES})\\s{0,8}[:=]\\s{0,8})[^\\s&#"',;]*`, "gi");
const CREDENTIAL_JSON_PAIR = new RegExp(`("(?:${CREDENTIAL_NAMES})"\\s{0,8}:\\s{0,8})"[^"]*"`, "gi");
const URL_USERINFO = /(\b[a-z][a-z0-9+.-]{1,20}:\/\/)[^\s/@:]{1,128}:[^\s/@]{1,256}@/gi;
const PEM_PRIVATE_KEY =
  /-----BEGIN [A-Z ]{0,30}PRIVATE KEY-----(?:[\s\S]{0,4096}?-----END [A-Z ]{0,30}PRIVATE KEY-----|[\s\S]{0,4096})/g;
// A header written in text: the value runs to the end of the line.
const HEADER_LINE =
  /(?<![A-Za-z0-9_-])((?:Cookie|Set-Cookie|Authorization|Proxy-Authorization|x-[A-Za-z0-9-]{0,40}(?:token|key|secret|password))\s{0,8}:\s{0,8})[^\r\n]*/gi;
const PATH_TOKEN =
  /(\/(?:verify|reset|confirm|invite|token|auth|magic|unsubscribe)[A-Za-z0-9_-]{0,30}\/)(?:[a-fA-F0-9]{32,}|[A-Za-z0-9_-]{40,})(?=[/?#\s"']|$)/gi;
const AUTH_SCHEME_TOKEN = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
const CARD_CANDIDATE = /\b\d(?:[ -]?\d){12,18}\b/g;

const SECRET_KEY_SUFFIX = /(?:token|key|secret|password|authorization|cookie)$/;
const SECRET_KEY_EXACT = new Set(["passwd", "pwd", "cardnumber", "cvc", "cvv"]);
const BODY_KEYS = new Set(["requestBody", "responseBody"]);

const scrubbedBreadcrumbs = new WeakSet<object>();

const redactor = createRedactor({
  textRules: [
    { pattern: PEM_PRIVATE_KEY, replacement: REDACTED },
    { pattern: HEADER_LINE, replacement: `$1${REDACTED}` },
    { pattern: URL_USERINFO, replacement: `$1${REDACTED}@` },
    ...TOKEN_PATTERNS.map((pattern) => ({ pattern, replacement: REDACTED })),
    { pattern: PATH_TOKEN, replacement: "$1[token]" },
    { pattern: CREDENTIAL_JSON_PAIR, replacement: `$1"${REDACTED}"` },
    { pattern: CREDENTIAL_PAIR, replacement: `$1${REDACTED}` },
    { pattern: AUTH_SCHEME_TOKEN, replacement: `$1 ${REDACTED}` },
    { pattern: CARD_CANDIDATE, replacement: maskCardNumber },
  ],
  maxTextLength: MAX_STRING_LENGTH,
  truncatedMark: "…[truncated]",
  isSecretEntry: (key, item) => isSecretKey(key) || (key.toLowerCase() === "code" && typeof item === "string"),
  maxDepth: MAX_DEPTH,
});

function isSecretKey(key: string): boolean {
  const name = key.toLowerCase().replace(/[-_]/g, "");
  return SECRET_KEY_EXACT.has(name) || SECRET_KEY_SUFFIX.test(name);
}

function maskCardNumber(candidate: string): string {
  const digits = candidate.replace(/[ -]/g, "");
  return digits.length >= 13 && digits.length <= 19 && passesLuhn(digits) ? "[card]" : candidate;
}

/** Redacts credentials and Luhn-valid card numbers; emails, names and file names stay visible. */
export function scrubString(value: string): string {
  return redactor.redactSecrets(value);
}

/** Deep-scrubs a data bag: credential keys are replaced, every string value goes through scrubString. */
export function scrubData<T>(value: T, depth = 0): T {
  return redactor.redactValue(value, depth);
}

function scrubBreadcrumbUnsafe(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data && Object.fromEntries(Object.entries(breadcrumb.data).filter(([key]) => !BODY_KEYS.has(key)));
  const scrubbed: Breadcrumb = {
    ...breadcrumb,
    ...(breadcrumb.message !== undefined && { message: scrubString(String(breadcrumb.message)) }),
    ...(data && { data: scrubData(data) }),
  };
  scrubbedBreadcrumbs.add(scrubbed);
  return scrubbed;
}

/** Scrubs a breadcrumb's message and data and drops request and response bodies; null (drop it) if scrubbing throws. */
export const scrubBreadcrumb: (breadcrumb: Breadcrumb) => Breadcrumb | null = failClosed(scrubBreadcrumbUnsafe, () => null);

/** Scrubs a streamed span's name and attributes; a span that cannot be scrubbed is sent without them. */
export const scrubSpan: (span: StreamedSpan) => StreamedSpan = failClosed(
  (span: StreamedSpan): StreamedSpan => ({ ...span, name: scrubString(span.name), attributes: scrubData(span.attributes) }),
  (span) => ({ ...span, name: REDACTED, attributes: {} }),
);

/** Scrubs a log's message and attributes; null (drop it) if scrubbing throws. */
export const scrubLog: (log: LogEntry) => LogEntry | null = failClosed(
  (log: LogEntry): LogEntry => ({
    ...log,
    message: scrubString(String(log.message)),
    ...(log.attributes && { attributes: scrubData(log.attributes) }),
  }),
  () => null,
);

function scrubEventUnsafe(event: ErrorEvent): ErrorEvent {
  const scrubbed: ErrorEvent = { ...event };
  if (typeof scrubbed.message === "string") scrubbed.message = scrubString(scrubbed.message);
  if (typeof scrubbed.transaction === "string") scrubbed.transaction = scrubString(scrubbed.transaction);
  if (scrubbed.logentry) scrubbed.logentry = scrubData(scrubbed.logentry);
  if (scrubbed.extra) scrubbed.extra = scrubData(scrubbed.extra);
  if (scrubbed.contexts) scrubbed.contexts = scrubData(scrubbed.contexts);
  if (scrubbed.tags) scrubbed.tags = scrubData(scrubbed.tags);
  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map((b) => (scrubbedBreadcrumbs.has(b) ? b : scrubBreadcrumbUnsafe(b)));
  }
  if (scrubbed.request) {
    const { cookies, ...request } = scrubData(scrubbed.request);
    scrubbed.request = {
      ...request,
      ...(cookies && { cookies: Object.fromEntries(Object.keys(cookies).map((name) => [name, REDACTED])) }),
    };
  }
  const exceptions = scrubbed.exception?.values;
  if (scrubbed.exception && exceptions) {
    scrubbed.exception = {
      ...scrubbed.exception,
      values: exceptions.map((v) => (typeof v.value === "string" ? { ...v, value: scrubString(v.value) } : v)),
    };
  }
  return scrubbed;
}

/** Scrubs an error event and leaves user and stack frames intact; null (drop it) if scrubbing throws. */
export function scrubEvent(event: ErrorEvent): ErrorEvent | null {
  try {
    return scrubEventUnsafe(event);
  } catch {
    return null;
  }
}
